import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form, Select, DatePicker, Input, Button, message, AutoComplete } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import purchaseApi from '../../../../services/process/purchase';
import ResourceUploader from '../../../../components/Business/ResourceUploader';

interface UploadPaymentVoucherModalProps {
  open: boolean;
  onCancel: () => void;
  selectedCount: number;
  selectedIds: string[];
  onSuccess: () => void;
}

const payeeOptions = [
  { value: '付明兴', account: '13213191262' },
  { value: '张一帆', account: '13613818859' },
];

const UploadPaymentVoucherModal: React.FC<UploadPaymentVoucherModalProps> = ({
  open,
  onCancel,
  selectedCount,
  selectedIds,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const handlePayeeChange = (value: string) => {
    const option = payeeOptions.find(opt => opt.value === value);
    if (option) {
      form.setFieldsValue({ payeeAccount: option.account });
    }
  };

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    try {
      const values = await form.validateFields();
      if (!values.voucherUrl || values.voucherUrl.length === 0) {
        message.error('请上传凭证文件');
        submittingRef.current = false;
        setSubmitting(false);
        return;
      }

      await purchaseApi.batchChangeStatus(selectedIds, 'upload_payment_voucher', {
        payment_method: values.paymentMethod,
        payment_date: values.paymentDate.format('YYYY-MM-DD'),
        payee: values.payee,
        payee_account: values.payeeAccount, // Keep generic account field
        voucher_url: values.voucherUrl[0], // Take the first file
      });

      message.success('上传成功');
      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) {
        // Validation failed
        submittingRef.current = false;
        setSubmitting(false);
      } else {
        console.error(error);
        message.error('提交失败');
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={600}
      closeIcon={null}
      styles={{ 
        body: { padding: 0, borderRadius: 28, overflow: 'hidden' }, 
        content: { borderRadius: 28, padding: 0, overflow: 'hidden' } 
      }}
      centered
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', position: 'relative', width: '100%', background: 'white' }}>
        {/* Header Section */}
        <div style={{ background: '#f0f7ff', borderBottom: '1px solid #e2eefa', padding: '24px 28px 14px', width: '100%' }}>
           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
             <div style={{ display: 'flex', alignItems: 'center', color: '#155dfc', fontSize: 24, fontWeight: 'bold' }}>
               <span>{selectedCount > 1 ? '批量上传支付凭证' : '上传支付凭证'}</span>
             </div>
             <div onClick={onCancel} style={{ cursor: 'pointer', fontSize: 24, color: '#4b5563' }}>
               <CloseOutlined />
             </div>
           </div>
           
           <div style={{ background: 'rgba(43,110,240,0.06)', borderRadius: 30, padding: '4px 16px', display: 'inline-flex', alignItems: 'center' }}>
             <span style={{ color: '#3f5575', fontSize: 15, fontWeight: 'bold' }}>您正在为 </span>
             <span style={{ background: '#155dfc', color: 'white', borderRadius: 10, padding: '0 8px', margin: '0 4px', fontSize: 15, fontWeight: 600 }}>{selectedCount}</span>
             <span style={{ color: '#3f5575', fontSize: 15, fontWeight: 'bold' }}> 个订单上传支付凭证</span>
           </div>
        </div>

        {/* Form Section */}
        <div style={{ padding: '28px', width: '100%' }}>
          <Form form={form} layout="vertical">
            <Form.Item 
              label={<span style={{ color: '#152f4a', fontWeight: 'bold', fontSize: 15 }}>付款方式</span>} 
              name="paymentMethod" 
              rules={[{ required: true, message: '请选择付款方式' }]}
            >
              <Select 
                placeholder="请选择" 
                size="large"
                style={{ borderRadius: 16 }}
                options={[
                  { label: '企业支付宝', value: '企业支付宝' },
                  { label: '浙商银行', value: '浙商银行' },
                  { label: '杭州银行', value: '杭州银行' },
                  { label: '代运营备用金', value: '代运营备用金' },
                ]}
              />
            </Form.Item>

            <Form.Item 
              label={<span style={{ color: '#152f4a', fontWeight: 'bold', fontSize: 15 }}>付款日期</span>} 
              name="paymentDate" 
              rules={[{ required: true, message: '请选择付款日期' }]}
            >
              <DatePicker style={{ width: '100%', borderRadius: 16 }} size="large" placeholder="请选择日期" />
            </Form.Item>

            <Form.Item 
              label={<span style={{ color: '#152f4a', fontWeight: 'bold', fontSize: 15 }}>收款人</span>} 
              name="payee" 
              rules={[{ required: true, message: '请选择收款人' }]}
            >
              <Select
                options={payeeOptions.map(opt => ({ label: opt.value, value: opt.value }))}
                onChange={handlePayeeChange}
                placeholder="请选择"
                size="large"
                style={{ borderRadius: 16 }}
              />
            </Form.Item>

            <Form.Item 
              label={<span style={{ color: '#152f4a', fontWeight: 'bold', fontSize: 15 }}>收款账号</span>} 
              name="payeeAccount" 
            >
              <Input placeholder="请选择收款人自动填充" size="large" style={{ borderRadius: 16 }} disabled />
            </Form.Item>

            <div style={{ 
              background: '#f9fcff', 
              border: '2px dashed #bdd6f0', 
              borderRadius: 24, 
              padding: 16,
              textAlign: 'center' 
            }}>
              <Form.Item name="voucherUrl" noStyle>
                 <ResourceUploader 
                   dir="payment-vouchers"
                   maxCount={1}
                   allowedTypes={['.jpg', '.png', '.pdf']}
                   initialRender="dropzone"
                 />
              </Form.Item>
            </div>
          </Form>
        </div>

        {/* Footer Section */}
        <div style={{ 
          borderTop: '1px solid #e7f0fe', 
          padding: '17px 28px 28px', 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 16 
        }}>
          <Button 
            size="large" 
            style={{ borderRadius: 40, color: '#1a3f6e', borderColor: '#bed6f2', padding: '0 30px', height: 48 }}
            onClick={onCancel}
          >
            取消
          </Button>
          <Button 
            type="primary" 
            size="large" 
            loading={submitting}
            onClick={handleSubmit}
            style={{ 
              borderRadius: 40, 
              background: '#155dfc', 
              boxShadow: '0 4px 10px rgba(43,110,240,0.3)',
              padding: '0 30px',
              height: 48
            }}
          >
            确认上传
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadPaymentVoucherModal;
